/** Pure truth-table function for the import-run status display (GIS Phase 2A
 *  Task 12 / C3). Replaces the imports dashboard's unqualified "running"
 *  badge, which hid a stuck legacy walk for days during the 2026-07-21
 *  outage -- a genuinely-live run and a run stuck for a week rendered
 *  IDENTICALLY. Every branch below is one row of the design spec's truth
 *  table (docs/superpowers/specs/2026-07-24-gis-queue-2a-design.md §6),
 *  copied here VERBATIM as the source of truth:
 *
 * | DB state | display |
 * |---|---|
 * | `checkpoint IS NULL`, status completed | **Legacy sync (pre-walk) — completed** |
 * | `checkpoint IS NULL`, status failed | **Legacy sync (pre-walk) — failed** |
 * | `checkpoint IS NULL`, status running, `started_at` older than 24 h | **Legacy sync — stale (started `<date>`)** — R2-4: the reported bug state; NEVER rendered as active |
 * | `checkpoint IS NULL`, status running, recent | **Legacy sync — running** (only a genuinely-live legacy execution) |
 * | checkpoint NOT NULL < published, status running, gate ACTIVE | **In landing walk — at `<checkpoint>`** |
 * | checkpoint NOT NULL < published, status running, gate inactive | **Awaiting apply — `<checkpoint>`** (e.g. validated/approved resting state) |
 * | status failed, checkpoint NOT NULL | **Failed at `<checkpoint>` (resumable)** — gate flag shown if armed |
 * | checkpoint published, status running | **Confirmation pending** |
 * | checkpoint published, status completed | **Completed (generation N)** |
 *
 * Row order matters and is NOT the table's top-to-bottom order: "status
 * failed, checkpoint NOT NULL" applies regardless of whether that checkpoint
 * is "published" or an earlier step (the confirmation pass itself can fail
 * AT checkpoint='published' -- see scripts/land_gis_export.py), so the failed
 * check runs before the checkpoint==='published' branch below.
 *
 * Checkpoint vocabulary (scripts/land_gis_export.py::CHECKPOINTS, ordered):
 * staged, validated, approved, reconciling, merging_entity, merging_element,
 * linking, recomputing_derived, recomputing_secondary, recomputing_protection,
 * matching, published. "published" is the terminal walk step; every other
 * value is "< published" for this table's purposes.
 */

export type RunTone = 'neutral' | 'active' | 'warn' | 'done' | 'fail'

export type RunStatusInput = {
  checkpoint: string | null
  status: string
  generation: number | null
  maintenance_gate_active: boolean
  started_at: string
}

export type RunDisplayStatus = {
  label: string
  tone: RunTone
}

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000

/** `YYYY-MM-DD` (UTC) -- deterministic regardless of the viewer's local
 *  timezone or locale, so the stale-run label is stable in tests and across
 *  operators in different timezones alike. */
function formatStartedDate(startedAt: string): string {
  return new Date(startedAt).toISOString().slice(0, 10)
}

/** Row 1-4: `checkpoint IS NULL` -- the legacy (pre-walk) sync family. */
function legacyRunStatus(run: RunStatusInput, now: Date): RunDisplayStatus {
  if (run.status === 'completed') {
    return { label: 'Legacy sync (pre-walk) — completed', tone: 'done' }
  }
  if (run.status === 'failed') {
    return { label: 'Legacy sync (pre-walk) — failed', tone: 'fail' }
  }
  if (run.status === 'running') {
    const ageMs = now.getTime() - new Date(run.started_at).getTime()
    if (ageMs > STALE_THRESHOLD_MS) {
      // R2-4: the reported bug state. Deliberately never the bare word
      // "running" -- the started date is the whole point of this row.
      return {
        label: `Legacy sync — stale (started ${formatStartedDate(run.started_at)})`,
        tone: 'warn',
      }
    }
    return { label: 'Legacy sync — running', tone: 'active' }
  }
  // No row of the table covers another legacy status value; fall back to a
  // neutral, still-truthful label rather than guessing.
  return { label: `Legacy sync — ${run.status}`, tone: 'neutral' }
}

/** Rows 5-9: `checkpoint IS NOT NULL` -- the walk-era (checkpointed landing
 *  pipeline) family. */
function walkRunStatus(run: RunStatusInput): RunDisplayStatus {
  const checkpoint = run.checkpoint as string

  // "status failed, checkpoint NOT NULL" applies at ANY checkpoint,
  // including 'published' (confirmation can fail there) -- checked first,
  // ahead of the checkpoint==='published' branch below.
  if (run.status === 'failed') {
    const gateNote = run.maintenance_gate_active ? ' — gate armed' : ''
    return { label: `Failed at ${checkpoint} (resumable)${gateNote}`, tone: 'fail' }
  }

  if (checkpoint === 'published') {
    if (run.status === 'running') {
      return { label: 'Confirmation pending', tone: 'warn' }
    }
    if (run.status === 'completed') {
      return { label: `Completed (generation ${run.generation})`, tone: 'done' }
    }
  }

  if (run.status === 'running') {
    if (run.maintenance_gate_active) {
      return { label: `In landing walk — at ${checkpoint}`, tone: 'active' }
    }
    return { label: `Awaiting apply — ${checkpoint}`, tone: 'neutral' }
  }

  // No row of the table covers another walk-era combination (e.g. a
  // checkpoint short of 'published' with status='completed', which the walk
  // never produces); fall back to a neutral, still-truthful label.
  return { label: `${run.status} at ${checkpoint}`, tone: 'neutral' }
}

/** The C3 truth table, dispatched on `checkpoint IS NULL` (legacy family) vs
 *  not (walk-era family) -- see the module comment for the full table. */
export function runDisplayStatus(run: RunStatusInput, now: Date): RunDisplayStatus {
  if (run.checkpoint === null) {
    return legacyRunStatus(run, now)
  }
  return walkRunStatus(run)
}
